import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import ResultsSuccessView from '../components/ResultsSuccessView';
import useMatchResults from '../hooks/useMatchResults';
import { trackPaymentCompleted } from '../lib/paymentAnalytics';
import { safeLocalStorage } from '../lib/safeStorage';

export default function ResultsPage() {
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const tokenFromUrl = searchParams.get('t');
  if (tokenFromUrl) {
    safeLocalStorage.setItem(`quiz_token_${sessionId}`, tokenFromUrl);
  }

  const partnerToken = safeLocalStorage.getItem(`quiz_token_${sessionId}`);
  const { data, isLoading, error, refetch } = useMatchResults(sessionId, partnerToken);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let needsClean = false;

    if (searchParams.has('t')) {
      searchParams.delete('t');
      needsClean = true;
    }

    if (searchParams.get('payment') === 'success') {
      trackPaymentCompleted();
      setToast('Payment confirmed! Loading your full results...');
      searchParams.delete('payment');
      searchParams.delete('stripe_session_id');
      needsClean = true;
      refetch();
      toastTimeoutRef.current = setTimeout(() => setToast(null), 5000);
    }

    if (needsClean) {
      setSearchParams(searchParams, { replace: true });
    }

    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handlePaymentComplete(reason: 'payment-completed' | 'already-paid') {
    if (reason === 'payment-completed') {
      trackPaymentCompleted();
      setToast('Payment confirmed! Loading your full results...');
    } else {
      setToast('Your full results are already unlocked.');
    }

    refetch();
    toastTimeoutRef.current = setTimeout(() => setToast(null), 5000);
  }

  if (!partnerToken) {
    return (
      <AppShell showBack backTo="/" title="Results">
        <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-4">
          <div className="text-5xl">🔑</div>
          <h1 className="text-2xl font-bold text-white">
            Can&apos;t access results
          </h1>
          <p className="text-slate-400">
            Open this page in the same browser where you took the quiz.
            Your access token is stored locally and can&apos;t be transferred.
          </p>
        </div>
      </AppShell>
    );
  }

  if (isLoading) {
    return (
      <AppShell showBack backTo="/" title="Results">
        <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-4">
          <div className="text-5xl animate-pulse">🔮</div>
          <h1 className="text-xl font-bold text-white">
            Computing your matches...
          </h1>
          <p className="text-slate-400">
            Finding everything you both want.
          </p>
        </div>
      </AppShell>
    );
  }

  if (error || !data) {
    return (
      <AppShell showBack backTo="/" title="Results">
        <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-4">
          <div className="text-5xl">😕</div>
          <h1 className="text-2xl font-bold text-white">
            Something went wrong
          </h1>
          <p className="text-slate-400">
            {error || 'Could not load results.'}
          </p>
          <button
            onClick={refetch}
            className="px-6 py-3 gradient-cta text-white font-semibold rounded-xl transition-all"
          >
            Try again
          </button>
        </div>
      </AppShell>
    );
  }

  if ('status' in data && (data as unknown as { status: string }).status === 'waiting') {
    return (
      <AppShell showBack backTo="/" title="Results">
        <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-4">
          <div className="text-5xl animate-pulse">⏳</div>
          <h1 className="text-xl font-bold text-white">
            Waiting for your partner
          </h1>
          <p className="text-slate-400">
            Both partners need to finish the quiz before results can be computed.
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell showBack backTo="/" title="Results">
      {toast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-green-500 text-white rounded-xl shadow-lg text-sm font-medium max-w-[calc(100vw-2rem)]">
          {toast}
        </div>
      )}

      <ResultsSuccessView
        data={data}
        partnerToken={partnerToken}
        resultsUrl={`${window.location.origin}/results/${sessionId}?t=${partnerToken}`}
        onPaymentComplete={handlePaymentComplete}
      />
    </AppShell>
  );
}
